import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import UploadWithLabel from "./uploadWithLable";
import TwoFieldRow from "./twoFieldsRow";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../api/axiosInterceptor";
import { useTranslation } from "react-i18next";

function BaseInfoComponent({
  base,
  feOptions,
  onFeSelect,
  accountTypes,
  claimentTypes,
  setBase,
  activeTab,
  setActiveTab,
  onClaimantTypeChange,
}) {
  const { t } = useTranslation();

  const [relationDropdownValue, setRelationDropdownValue] = useState("");
  const [relationSpecifyValue, setRelationSpecifyValue] = useState("");
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    phone_number: "",
  });

  const formik = useFormik({
    initialValues: {
      ...base,
      name_of_claimant1: "",
      email_1: "",
      ph_no: "",
      delayed_ord_no: "",
      delayed_ord_docs: null,
    },
    validationSchema: Yup.object({
      fe_name: Yup.string().required(t("baseInfo.validation.feNameRequired")),
      ca_designation: Yup.string().required(
        t("baseInfo.validation.caDesignationRequired")
      ),
      fe_code: Yup.string().required(t("baseInfo.validation.feCodeRequired")),
      account_type: Yup.string().required(
        t("baseInfo.validation.accountTypeRequired")
      ),
      claiment_type: Yup.string().required(
        t("baseInfo.validation.claimentTypeRequired")
      ),
      name_of_claimant1: Yup.string().required(
        t("baseInfo.validation.claimantNameRequired")
      ),
      father_husband_name: Yup.string().required(
        t("baseInfo.validation.fatherHusbandRequired")
      ),
      address: Yup.string().required(t("baseInfo.validation.addressRequired")),
      date_of_birth: Yup.string()
        .required(t("baseInfo.validation.dobRequired"))
        .test(
          "age-validation",
          "Date of birth must be at least 6 years ago",
          function (value) {
            const today = new Date();
            const sixYearsAgo = new Date(today.getFullYear() - 6, today.getMonth(), today.getDate());
            return value && new Date(value) <= sixYearsAgo;
          }
        ),
      ph_no: Yup.string().required(t("baseInfo.validation.phoneRequired")),
      // pan_no: Yup.string()
      //   .required(t("baseInfo.validation.panNoRequired"))
      //   .matches(/^[A-Za-z0-9]{10}$/, t("baseInfo.validation.panNoInvalid")),

      pan_no: Yup.string()
      .required(t("baseInfo.validation.panNoRequired"))
      // .matches(/^[A-Z]{3}[A-HJ-NP-TV-Z]{1}\d{4}[A-Z]{1}$/, t("baseInfo.validation.panNoInvalid"))
      .length(10, t("baseInfo.validation.panNoInvalid")),

      pan_doc: Yup.mixed()
        .required(t("baseInfo.validation.panRequired"))
        .test(
          "fileExists",
          t("baseInfo.validation.panRequired"),
          (value) => value !== null && value !== undefined
        )
        .test(
          "fileType",
          "PAN document must be PDF only",
          (value) => !value || value.type === "application/pdf"
        )
        .test(
          "fileSize",
          t("baseInfo.validation.fileSizeExceeded"),
          (value) => !value || value.size <= 2 * 1024 * 1024
        ),
      email_1: Yup.string()
        .email(t("baseInfo.validation.invalidEmail1"))
        .required(t("baseInfo.validation.email1Required")),
      ekyc_doc: Yup.mixed()
        .required(t("baseInfo.validation.ekycRequired"))
        .test(
          "fileExists",
          t("baseInfo.validation.ekycRequired"),
          (value) => value !== null && value !== undefined
        )
        .test(
          "fileType",
          "eKYC document must be PDF only",
          (value) => !value || value.type === "application/pdf"
        )
        .test(
          "fileSize",
          t("baseInfo.validation.fileSizeExceeded"),
          (value) => !value || value.size <= 2 * 1024 * 1024
        ),
      photo: Yup.mixed()
        .required(t("baseInfo.validation.photoRequired"))
        .test(
          "fileExists",
          t("baseInfo.validation.photoRequired"),
          (value) => value !== null && value !== undefined
        )
        .test(
          "fileType",
          "Photo must be JPG, JPEG, PNG only",
          (value) =>
            !value ||
            ["image/jpeg", "image/jpg", "image/png"].includes(value.type)
        )
        .test(
          "fileSize",
          t("baseInfo.validation.fileSizeExceeded"),
          (value) => !value || value.size <= 2 * 1024 * 1024
        ),
      delayed_ord_no: Yup.string().when('is_delayed', {
        is: true,
        then: (schema) => schema.required("Court Order No. is required for delayed applications"),
        otherwise: (schema) => schema.nullable()
      }),
      delayed_ord_docs: Yup.mixed().when('is_delayed', {
        is: true,
        then: (schema) => schema
          .required("Upload Order Copy is required for delayed applications")
          .test(
            "fileExists",
            "Upload Order Copy is required",
            (value) => value !== null && value !== undefined
          )
          .test(
            "fileType",
            "Court order document must be PDF only",
            (value) => !value || value.type === "application/pdf"
          )
          .test(
            "fileSize",
            "File size must be less than 2MB",
            (value) => !value || value.size <= 2 * 1024 * 1024
          ),
        otherwise: (schema) => schema.nullable()
      }),
      alt_ph_no: Yup.string().matches(
        /^\d{10}$/,
        "Alternative mobile number must be exactly 10 digits"
      ),
      relation_with_owner: Yup.string().nullable(),
    }),
    enableReinitialize: true,
    onSubmit: async (values, { setFieldError }) => {
      const submitValues = {
        ...values,
        is_delayed: base.is_delayed,
        is_normal: base.is_normal,
        relation_with_owner: relationDropdownValue
          ? relationDropdownValue + (relationSpecifyValue ? `: ${relationSpecifyValue}` : "")
          : values.relation_with_owner,
      };

      try {
        const response = await api.get(
          "/claim-mgmt/duplicate-claim-checker/",
          { params: { fe_name: submitValues.fe_name, pan_number: submitValues.pan_no, applicant_type: submitValues.claiment_type } }
        );
        if (response.data.already_applied) {
          setFieldError("fe_name", t("baseInfo.validation.alreadyApplied"));
          return;
        }
        setBase(submitValues);
        setActiveTab(activeTab + 1);
      } catch (error) {
        setBase(submitValues);
        setActiveTab(activeTab + 1);
      }
    },
  });

  // Fetch user details once on mount
  useEffect(() => {
    async function fetchUserDetails() {
      try {
        const response = await api.get("/auth/logedin-user-details/");
        const data = response.data;
        setUserDetails({
          name: data.name || "",
          email: data.email || "",
          phone_number: data.phone_number || "",
        });
      } catch (error) {
        console.error("Failed fetching user details:", error);
      }
    }
    fetchUserDetails();
  }, []);

  // Auto-fill logic: Always auto-fill name, email, and phone for all claimant types
  // useEffect(() => {
  //   const { fe_name, claiment_type, account_type } = formik.values;
  //   if (fe_name && claiment_type && account_type && userDetails.name) {
  //     // Always auto-fill name, email, and phone for all claimant types
  //     formik.setFieldValue("name_of_claimant1", userDetails.name, false);
  //     formik.setFieldValue("email_1", userDetails.email, false);
  //     formik.setFieldValue("ph_no", userDetails.phone_number, false);
  //   }
  // }, [formik.values.fe_name, formik.values.claiment_type, formik.values.account_type, userDetails]);

  // Replace ONLY this useEffect block

// Auto-fill logic
useEffect(() => {
  const { fe_name, claiment_type, account_type } = formik.values;

  if (fe_name && claiment_type && account_type && userDetails.name) {

    // Always auto-fill email and phone
    formik.setFieldValue("email_1", userDetails.email, false);
    formik.setFieldValue("ph_no", userDetails.phone_number, false);

    // Auto-fill claimant name only for self
    if (claiment_type === "self") {
      formik.setFieldValue("name_of_claimant1", userDetails.name, false);
    } else {
      // Clear field for manual input
      formik.setFieldValue("name_of_claimant1", "", false);
    }
  }
}, [
  formik.values.fe_name,
  formik.values.claiment_type,
  formik.values.account_type,
  userDetails,
]);

  useEffect(() => {
    if (formik.values.relation_with_owner) {
      const idx = formik.values.relation_with_owner.indexOf(":");
      if (idx !== -1) {
        setRelationDropdownValue(formik.values.relation_with_owner.substring(0, idx).trim());
        setRelationSpecifyValue(formik.values.relation_with_owner.substring(idx + 1).trim());
      } else {
        setRelationDropdownValue(formik.values.relation_with_owner);
        setRelationSpecifyValue("");
      }
    }
  }, [formik.values.relation_with_owner]);

  const handleFileChange = (e, fieldName) => {
    const file = e.currentTarget.files[0] || null;
    formik.setFieldValue(fieldName, file);
    formik.setFieldTouched(fieldName, true);
  };

  const selectFormControlSX = {
    minWidth: 200,
    maxWidth: "100%",
  };

  const handleFeNameChange = (e) => {
    formik.setFieldValue("fe_name", e.target.value, true);
    onFeSelect(e);
  };

  const handleClaimantTypeChange = (e) => {
    formik.setFieldValue("claiment_type", e.target.value, true);
    onClaimantTypeChange(e.target.value);
  };

  const handleRelationDropdownChange = (e) => {
    setRelationDropdownValue(e.target.value);
    setRelationSpecifyValue("");
  };

  const areClaimantAndAccountSelected = formik.values.claiment_type && formik.values.account_type;

  return (
    <Container maxWidth={false} sx={{ mt: 5, maxWidth: 800 }}>
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
        <Typography
          variant="h4"
          fontWeight="700"
          color="primary.main"
          gutterBottom
          align="center"
        >
          {t("baseInfo.headingDepositorInfo")}
        </Typography>

        <Box component="form" noValidate onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <FormControl
              fullWidth
              margin="normal"
              error={formik.touched.fe_name && Boolean(formik.errors.fe_name)}
              sx={selectFormControlSX}
            >
              <InputLabel>{t("baseInfo.feName")} *</InputLabel>
              <Select
                name="fe_name"
                value={formik.values.fe_name}
                label={t("baseInfo.feName")}
                onChange={handleFeNameChange}
                onBlur={formik.handleBlur}
              >
                {feOptions.map((f) => (
                  <MenuItem key={f.fe_code || f.fe_name} value={f.fe_name}>
                    {f.fe_name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.fe_name && formik.errors.fe_name && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                  {formik.errors.fe_name}
                </Typography>
              )}
            </FormControl>

            <TwoFieldRow>
              <TextField
                label={t("baseInfo.caDesignation") + " *"}
                name="ca_designation"
                value={formik.values.ca_designation}
                InputProps={{ readOnly: true }}
                error={
                  formik.touched.ca_designation &&
                  Boolean(formik.errors.ca_designation)
                }
                helperText={
                  formik.touched.ca_designation && formik.errors.ca_designation
                }
                fullWidth
              />
              <TextField
                label={t("baseInfo.feCode") + " *"}
                name="fe_code"
                value={formik.values.fe_code}
                InputProps={{ readOnly: true }}
                error={formik.touched.fe_code && Boolean(formik.errors.fe_code)}
                helperText={formik.touched.fe_code && formik.errors.fe_code}
                fullWidth
              />
            </TwoFieldRow>

            <TwoFieldRow>
              <FormControlLabel
                control={<Checkbox checked={base.is_normal} />}
                label={t("baseInfo.isNormal") + " *"}
                disabled
              />
              <FormControlLabel
                control={<Checkbox checked={base.is_delayed} />}
                label={t("baseInfo.isDelayed") + " *"}
                disabled
              />
            </TwoFieldRow>

            <TwoFieldRow>
              <FormControl
                fullWidth
                error={
                  formik.touched.claiment_type && Boolean(formik.errors.claiment_type)
                }
                sx={selectFormControlSX}
              >
                <InputLabel>{t("baseInfo.claimantType")} *</InputLabel>
                <Select
                  name="claiment_type"
                  value={formik.values.claiment_type || ""}
                  label={t("baseInfo.claimantType")}
                  onChange={handleClaimantTypeChange}
                  onBlur={formik.handleBlur}
                >
                  {claimentTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.claiment_type && formik.errors.claiment_type && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {formik.errors.claiment_type}
                  </Typography>
                )}
              </FormControl>
              <FormControl
                fullWidth
                error={formik.touched.account_type && Boolean(formik.errors.account_type)}
                sx={selectFormControlSX}
              >
                <InputLabel>{t("baseInfo.accountType")} *</InputLabel>
                <Select
                  name="account_type"
                  value={formik.values.account_type}
                  label={t("baseInfo.accountType")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {accountTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.account_type && formik.errors.account_type && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {formik.errors.account_type}
                  </Typography>
                )}
              </FormControl>
            </TwoFieldRow>

            {base.is_delayed && areClaimantAndAccountSelected && (
              <>
                <TwoFieldRow>
                  <TextField
                    label="Court Order No. *"
                    name="delayed_ord_no"
                    value={formik.values.delayed_ord_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.delayed_ord_no && Boolean(formik.errors.delayed_ord_no)}
                    helperText={formik.touched.delayed_ord_no && formik.errors.delayed_ord_no}
                    fullWidth
                  />
                  <UploadWithLabel
                    label="Upload Order Copy (2MB) *"
                    name="delayed_ord_docs"
                    onChange={(e) => handleFileChange(e, "delayed_ord_docs")}
                    file={formik.values.delayed_ord_docs}
                    error={formik.touched.delayed_ord_docs && Boolean(formik.errors.delayed_ord_docs)}
                    helperText={formik.touched.delayed_ord_docs && formik.errors.delayed_ord_docs}
                  />
                </TwoFieldRow>
                {formik.touched.delayed_ord_no && formik.errors.delayed_ord_no && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {formik.errors.delayed_ord_no}
                  </Typography>
                )}
                {formik.touched.delayed_ord_docs && formik.errors.delayed_ord_docs && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {formik.errors.delayed_ord_docs}
                  </Typography>
                )}
              </>
            )}

            <TwoFieldRow>
              {/* <TextField
                label={t("baseInfo.nameOfClaimant") + " *"}
                name="name_of_claimant1"
                value={formik.values.name_of_claimant1}
                InputProps={{ readOnly: true }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.name_of_claimant1 &&
                  Boolean(formik.errors.name_of_claimant1)
                }
                helperText={
                  formik.touched.name_of_claimant1 && formik.errors.name_of_claimant1
                }
                fullWidth
              /> */}

              <TextField
  label={t("baseInfo.nameOfClaimant") + " *"}
  name="name_of_claimant1"
  value={formik.values.name_of_claimant1}
  InputProps={{
    readOnly: formik.values.claiment_type === "self",
  }}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  error={
    formik.touched.name_of_claimant1 &&
    Boolean(formik.errors.name_of_claimant1)
  }
  helperText={
    formik.touched.name_of_claimant1 &&
    formik.errors.name_of_claimant1
  }
  fullWidth
/>
              {formik.values.account_type === "joint" && (
                <TextField
                  label={t("baseInfo.nameOfClaimant2")}
                  name="name_of_claimant2"
                  value={formik.values.name_of_claimant2}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                />
              )}
            </TwoFieldRow>

            <TextField
              margin="normal"
              label={t("baseInfo.fatherHusbandName") + " *"}
              name="father_husband_name"
              value={formik.values.father_husband_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.father_husband_name &&
                Boolean(formik.errors.father_husband_name)
              }
              helperText={
                formik.touched.father_husband_name && formik.errors.father_husband_name
              }
              fullWidth
            />

            <TextField
              multiline
              rows={2}
              margin="normal"
              label={t("baseInfo.address") + " *"}
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
              fullWidth
            />

            <TwoFieldRow>
              <TextField
                type="date"
                label={t("baseInfo.dob") + " *"}
                name="date_of_birth"
                value={formik.values.date_of_birth}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={
                  formik.touched.date_of_birth && Boolean(formik.errors.date_of_birth)
                }
                helperText={formik.touched.date_of_birth && formik.errors.date_of_birth}
                fullWidth
              />
              <TextField
                label={t("baseInfo.mobileNo") + " *"}
                name="ph_no"
                value={formik.values.ph_no}
                InputProps={{ readOnly: true }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.ph_no && Boolean(formik.errors.ph_no)}
                helperText={formik.touched.ph_no && formik.errors.ph_no}
                fullWidth
              />
            </TwoFieldRow>

            <TwoFieldRow>
              <TextField
                label={t("baseInfo.altMobileNo")}
                name="alt_ph_no"
                value={formik.values.alt_ph_no}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.alt_ph_no && Boolean(formik.errors.alt_ph_no)}
                helperText={formik.touched.alt_ph_no && formik.errors.alt_ph_no}
                fullWidth
              />
              <TextField
                label={t("baseInfo.panNo") + " *"}
                name="pan_no"
                value={formik.values.pan_no}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.pan_no && Boolean(formik.errors.pan_no)}
                helperText={formik.touched.pan_no && formik.errors.pan_no}
                fullWidth
              />
            </TwoFieldRow>

            <UploadWithLabel
              label={t("baseInfo.photoUpload")}
              name="photo"
              isImage  
              onChange={(e) => handleFileChange(e, "photo")}
              file={formik.values.photo}
              error={formik.touched.photo && Boolean(formik.errors.photo)}
              helperText={formik.touched.photo && formik.errors.photo}
            />
            {formik.touched.photo && formik.errors.photo && !formik.values.photo && (
              <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                {formik.errors.photo}
              </Typography>
            )}

            <UploadWithLabel
              label={t("baseInfo.panUpload")}
              name="pan_doc"
              onChange={(e) => handleFileChange(e, "pan_doc")}
              file={formik.values.pan_doc}
              error={formik.touched.pan_doc && Boolean(formik.errors.pan_doc)}
              helperText={formik.touched.pan_doc && formik.errors.pan_doc}
            />
            {/* {formik.touched.pan_doc && formik.errors.pan_doc && (
              <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                {formik.errors.pan_doc}
              </Typography>
            )} */}
            {formik.touched.pan_doc && formik.errors.pan_doc && !formik.values.pan_doc && (
  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
    {formik.errors.pan_doc}
  </Typography>
)}

            <TwoFieldRow>
              <TextField
                label={t("baseInfo.email1") + " *"}
                name="email_1"
                value={formik.values.email_1}
                InputProps={{ readOnly: true }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email_1 && Boolean(formik.errors.email_1)}
                helperText={formik.touched.email_1 && formik.errors.email_1}
                fullWidth
              />
              <TextField
                label={t("baseInfo.altEmail")}
                name="email_2"
                value={formik.values.email_2}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
              />
            </TwoFieldRow>

            <FormControl fullWidth margin="normal" sx={selectFormControlSX}>
              <InputLabel>{t("baseInfo.ekycDocType")} *</InputLabel>
              <Select
                name="ekyc_doc_type"
                value={formik.values.ekyc_doc_type || ""}
                label={t("baseInfo.ekycDocType")}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="driving_license">{t("baseInfo.drivingLicense")}</MenuItem>
                <MenuItem value="passport">{t("baseInfo.passport")}</MenuItem>
              </Select>
            </FormControl>

            <UploadWithLabel
              label={t("baseInfo.ekycUpload")}
              name="ekyc_doc"
              onChange={(e) => handleFileChange(e, "ekyc_doc")}
              file={formik.values.ekyc_doc}
              error={formik.touched.ekyc_doc && Boolean(formik.errors.ekyc_doc)}
              helperText={formik.touched.ekyc_doc && formik.errors.ekyc_doc}
            />
            {formik.touched.ekyc_doc && formik.errors.ekyc_doc && !formik.values.ekyc_doc &&(
              <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                {formik.errors.ekyc_doc}
              </Typography>
            )}

            <FormControl fullWidth margin="normal" sx={selectFormControlSX}>
              <InputLabel>{t("baseInfo.relationWithOwner")}</InputLabel>
              <Select value={relationDropdownValue} onChange={handleRelationDropdownChange} label={t("baseInfo.relationWithOwner")}>
                <MenuItem value="">
                  <em>{t("baseInfo.none")}</em>
                </MenuItem>
                <MenuItem value="Family member">{t("baseInfo.familyMember")}</MenuItem>
                <MenuItem value="Employee">{t("baseInfo.employee")}</MenuItem>
                <MenuItem value="Director">{t("baseInfo.director")}</MenuItem>
                <MenuItem value="Others">{t("baseInfo.others")}</MenuItem>
              </Select>
            </FormControl>

            {relationDropdownValue && (
              <TextField
                margin="normal"
                label={t("baseInfo.specifyDetails")}
                value={relationSpecifyValue}
                onChange={(e) => setRelationSpecifyValue(e.target.value)}
                fullWidth
              />
            )}

            <Box mt={3} sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="outlined" onClick={() => activeTab > 0 && setActiveTab(activeTab - 1)}>
                {t("baseInfo.back")}
              </Button>
              <Button type="submit" variant="contained">
                {t("baseInfo.next")}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}

export default BaseInfoComponent;
